/**
 * Engraving Cart Module
 * Handles cart-specific engraving operations and service product sync
 */

class EngravingCart {
  constructor() {
    this.core = window.engravingCore;
    this.validationTimeout = null;
    this.init();
  }

  /**
   * Initialize cart module
   */
  init() {
    // Empty and non-engraved carts need no service-product reconciliation.
    // Later cart changes still trigger the listeners below.
    if (window.theme?.hasEngravedCartItems) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.validateCart());
      } else {
        this.validateCart();
      }
    }

    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.validateCart());

    // Listen for legacy cart validation events (remove buttons, etc.)
    // These are dispatched by the theme's cart JS after quantity changes/removals.
    document.addEventListener('cart:validate', () => this.validateCart());
    
    // Listen for engraving changes
    document.addEventListener('engraving:change', () => {
      // Cart will be validated after engraving is added/removed
    });
  }

  /**
   * Validate cart engraving state
   */
  async validateCart() {
    // Debounce validations to allow cart updates (/cart/change.js) to complete
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
    }

    this.validationTimeout = setTimeout(async () => {
      try {
        if (!this.core) {
          console.warn('Engraving core not available');
          return;
        }

        await this.core.syncEngravingServiceProduct();
      } catch (error) {
        console.error('Failed to validate cart:', error);
      }
    }, 250);
  }

  /**
   * Add engravings to cart item
   * @param {string} lineItemKey - Cart line item key
   * @param {Array} engravings - Array of engraving objects
   * @param {number} quantity - Item quantity
   * @returns {Promise<boolean>} Success status
   */
  async addEngravings(lineItemKey, engravings, quantity) {
    try {
      if (!this.core) {
        throw new Error('Engraving core not available');
      }

      const success = await this.core.addEngravingsToLineItem(lineItemKey, engravings, quantity);
      
      if (success) {
        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Reload cart if needed
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }
      }

      return success;
    } catch (error) {
      console.error('Failed to add engravings to cart:', error);
      alert('Failed to add engravings. Please try again.');
      return false;
    }
  }

  /**
   * Remove engravings from cart item
   * @param {string} lineItemKey - Cart line item key
   * @param {Array} engravingsToRemove - Array of engraving objects to remove
   * @param {number} quantity - Item quantity
   * @returns {Promise<boolean>} Success status
   */
  async removeEngravings(lineItemKey, engravingsToRemove, quantity) {
    try {
      if (!this.core) {
        throw new Error('Engraving core not available');
      }

      const success = await this.core.removeEngravingsFromLineItem(
        lineItemKey,
        engravingsToRemove,
        quantity
      );

      if (success) {
        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Reload cart
        window.location.reload();
      }

      return success;
    } catch (error) {
      console.error('Failed to remove engravings from cart:', error);
      alert('Failed to remove engravings. Please try again.');
      return false;
    }
  }

  /**
   * Get engravings for a cart item
   * @param {Object} cartItem - Cart item object
   * @returns {Array} Array of engraving objects
   */
  getItemEngravings(cartItem) {
    if (!this.core) {
      return [];
    }

    return this.core.parseEngravingsFromProperties(cartItem.properties);
  }

  /**
   * Format engraving for display
   * @param {Object} engraving - Engraving object
   * @returns {string} Formatted string
   */
  formatEngravingForDisplay(engraving) {
    const side = engraving.side.charAt(0).toUpperCase() + engraving.side.slice(1);
    return `Blade ${engraving.blade} ${side}`;
  }

  /**
   * Calculate engraving cost for item
   * @param {Array} engravings - Array of engraving objects
   * @returns {number} Total cost in cents
   */
  calculateEngravingCost(engravings) {
    if (!this.core || !engravings || engravings.length === 0) {
      return 0;
    }

    const serviceProduct = this.core.getServiceProductInfo();
    if (!serviceProduct || !serviceProduct.price) {
      return 0;
    }

    return serviceProduct.price * engravings.length;
  }

  /**
   * Format price for display
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price
   */
  formatPrice(priceInCents) {
    // This is a simple formatter - you may want to use Shopify's money format
    return (priceInCents / 100).toFixed(2);
  }
}

// Initialize and export
window.EngravingCart = EngravingCart;
window.engravingCart = window.engravingCart || new EngravingCart();

